import { CSSProperties, useEffect, useMemo, useState } from 'react'
import {
  Box,
  UnstyledButton,
  Menu,
  Image,
  Group,
  useMantineTheme,
} from '@mantine/core'
import { TbChevronDown } from 'react-icons/tb'
import { useGetLanguages } from 'api/translations'
import { useTranslation } from 'react-i18next'
import { api } from 'api/api'
import { useQueryClient } from '@tanstack/react-query'
import classes from './PickerControl.module.css'

export function LanguagePicker() {
  const queryClient = useQueryClient()
  const [opened, setOpened] = useState(false)
  const theme = useMantineTheme()
  const { data, isLoading, isSuccess } = useGetLanguages()
  const { i18n } = useTranslation()
  const handleSelect = (id: string) => () => i18n.changeLanguage(id)
  const isDark = theme.colorScheme === 'dark'
  const controlStyle = {
    '--picker-border-color': isDark
      ? 'var(--cl-app-border)'
      : theme.colors.gray[2],
    '--picker-border-radius': theme.radius.md,
    '--picker-background-color': isDark
      ? opened
        ? 'var(--cl-app-surface-subtle)'
        : 'var(--cl-app-surface-elevated)'
      : opened
      ? theme.colors.gray[0]
      : theme.white,
    '--picker-hover-background-color': isDark
      ? 'var(--cl-app-surface-subtle)'
      : theme.colors.gray[0],
    '--picker-icon-rotation': opened ? '180deg' : '0deg',
  } as CSSProperties

  useEffect(() => {
    api.defaults.headers.common['Accept-Language'] = i18n?.language
    queryClient.invalidateQueries()
  }, [i18n?.language])

  const items = useMemo(
    () =>
      data && isSuccess
        ? Array.from(data).map(([code, { name }]) => {
            return (
              <Menu.Item
                leftSection={<Flag code={code} name={name} />}
                onClick={handleSelect(code)}
                key={name}
              >
                {name}
              </Menu.Item>
            )
          })
        : [],
    [data, isSuccess],
  )

  return (
    <Menu
      disabled={isLoading}
      onOpen={() => setOpened(true)}
      onClose={() => setOpened(false)}
      radius="md"
      width="target"
      transitionProps={{
        transition: 'pop-top-right',
      }}
    >
      <Menu.Target>
        <UnstyledButton
          className={`${classes.control} ${classes.languageControl}`}
          style={controlStyle}
        >
          {data && isSuccess && (
            <Group gap="xs">
              <Flag code={i18n.language} name={i18n.language} />
              <span className={classes.label}>
                {data.get(i18n.language)?.name}
              </span>
            </Group>
          )}
          <TbChevronDown size={16} className={classes.icon} />
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>{items}</Menu.Dropdown>
    </Menu>
  )
}

export const Flag = ({ code, name }: { code: string; name: string }) => {
  const flagCode = code.toLocaleLowerCase().split('-').at(-1)

  return (
    <Box
      component="span"
      style={{
        borderRadius: 9999,
        display: 'inline-flex',
        flex: '0 0 18px',
        height: 18,
        overflow: 'hidden',
        width: 18,
      }}
    >
      <Image
        src={`https://flagcdn.com/${flagCode}.svg`}
        alt={name}
        h={18}
        w={18}
        fit="cover"
        style={{
          display: 'block',
          flex: '0 0 18px',
          height: 18,
          width: 18,
        }}
      />
    </Box>
  )
}
